import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import { IAddDailyTask } from './DailyTask.props';

const useEmpDailyTask = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [formData, setFormData] = useState<IAddDailyTask>({
    todayUpdate: "",
    tomorrowUpdate: "",
  });
  const [localTaskHours, setLocalTaskHours] = useState<Record<string, number>>(
    {}
  );
  const [localTaskMinute, setLocalTaskMinute] = useState<
    Record<string, number>
  >({});
  const fetchData = async () => {
    if (!date) return;
    setLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getEmpDailyTasks}${date}`,
      });
      setData(response);
      setFormData({
        todayUpdate: response[0]?.todayUpdate,
        tomorrowUpdate: response[0]?.tomorrowUpdate,
      });
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (sendSlackMessage: boolean = false) => {
    const { displayHours, displayMinutes } = calculateTotalHoursAndMinutes();
    const totalMinutes = displayHours * 60 + displayMinutes;

    if (totalMinutes < 510) {
      // 8 hours 30 minutes = 510 minutes
      toast.error(
        "Warning: You have not completed 8 hours 30 minutes of work!",
        {
          duration: 3000,
        }
      );
    }

    try {
      await ApiService({
        method: "POST",
        endpoint: `${apiPaths.createEmpDailyTasks}`,
        data: formData,
      });
      for (const [taskId, updatedHours] of Object.entries(localTaskHours)) {
        await submitHours(taskId, updatedHours);
      }
      
      // Call Slack API only if explicitly requested (when user clicks Save Updates)
      if (sendSlackMessage) {
        try {
          const token = localStorage.getItem('accessToken');
          if (token) {
            const decodedToken: any = jwtDecode(token);
            const userId = decodedToken.userId;
            
            if (userId) {
              const userData = await ApiService({
                method: 'GET',
                endpoint: `${apiPaths.getAllEmployees}/${userId}`,
              });
              
              if (userData?.slackChannelId) {
                await ApiService({
                  method: "POST",
                  endpoint: apiPaths.slackChatPostMessage,
                  data: {
                    channel: userData.slackChannelId,
                  },
                });
              }
            }
          }
        } catch (slackError) {
          // Log error but don't fail the entire update
          console.error("Error sending Slack message:", slackError);
        }
      }
      
      toast.success("Daily Task Sheet updated successfully", {
        duration: 3000,
      });
    } catch (error) {
      console.error("Error submitting data:", error);
      toast.error("Error submitting data", {
        duration: 3000,
      });
    }
  };

  const submitHours = async (taskSheetId: string, hours: number) => {
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateEmpTaskSheet}${taskSheetId}`,
        data: { hours },
      });

      setData((prevData) =>
        prevData.map((taskData) => {
          if (taskData._id === taskSheetId) {
            return {
              ...taskData,
              todayTaskIds: taskData.todayTaskIds.map((task: any) => {
                if (task._id === taskSheetId) {
                  return { ...task, hours };
                }
                return task;
              }),
            };
          }
          toast.success("Daily Task hours updated successfully", {
            duration: 3000,
          });
          return taskData;
        })
      );
    } catch (error) {
      console.error("Error submitting hours:", error);
      toast.error("Error submitting hours", {
        duration: 3000,
      });
    }
  };

  const handleHoursChange = (taskId: string, newHours: number) => {
    setLocalTaskHours((prev) => ({
      ...prev,
      [taskId]: newHours,
    }));
  };
  const calculateTotalHoursAndMinutes = () => {
    let totalMinutes = 0;

    data.forEach((taskData) => {
      taskData.todayTaskIds.forEach((task: any) => {
        const localHours = localTaskHours[task._id];
        const taskHours = localHours ?? task.hours;
        totalMinutes += Math.round(taskHours * 60);
      });
    });

    const displayHours = Math.floor(totalMinutes / 60);
    const displayMinutes = totalMinutes % 60;

    return { displayHours, displayMinutes };
  };

  return {
    loading,
    data,
    fetchData,
    setDate,
    formData,
    setFormData,
    handleSubmit,
    submitHours,
    handleHoursChange,
    localTaskHours,
    calculateTotalHoursAndMinutes,
  };
};

export default useEmpDailyTask;
